import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../utils/api';
import { initAnalytics, identify } from '../utils/analytics';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Axios instance that always sends the auth token.
// Must use explicit baseURL — no base URL would hit the Vercel catch-all
// rewrite and return React app HTML instead of the Railway backend JSON.
const authAxios = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    withCredentials: true,
});
authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const response = await authAxios.get(`/auth/me`);
            setUser(response.data);
            identify(response.data._id, { role: response.data.role, plan: response.data.subscription_tier });
            setError(null);
        } catch (err) {
            console.error('Auth check failed:', err?.response?.status);
            localStorage.removeItem('token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        initAnalytics();
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const response = await api.post(`/api/auth/login`, { email, password });
            if (response.data.access_token) {
                localStorage.setItem('token', response.data.access_token);
            }
            setUser(response.data.user);
            setError(null);
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Login failed';
            setError(message);
            throw err; // Rethrow full error so callers can inspect status code
        }
    };

    const register = async (name, email, password, role = 'STUDENT') => {
        try {
            const response = await api.post(`/api/auth/register`, { name, email, password, role });
            setError(null);
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Registration failed';
            setError(message);
            throw new Error(message);
        }
    };

    const logout = async () => {
        try {
            await api.post(`/api/auth/logout`);
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            localStorage.removeItem('token');
            setUser(null);
        }
    };

    const updateRole = async (role) => {
        try {
            await authAxios.put(`/users/role`, { role });
            setUser(prev => ({ ...prev, role }));
            setError(null);
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to update role';
            setError(message);
            throw new Error(message);
        }
    };

    const resendVerification = async (email) => {
        try {
            const response = await api.post(`/api/auth/resend-verification`, { email });
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to resend verification email';
            throw new Error(message);
        }
    };

    const googleLogin = () => {
        window.location.href = `${BACKEND_URL}/api/auth/google`;
    };

    const setUserData = (userData) => {
        setUser(userData);
    };

    const refreshUser = async () => {
        try {
            const response = await authAxios.get(`/auth/me`);
            setUser(response.data);
            return response.data;
        } catch (err) {
            console.error('Failed to refresh user:', err);
            if (err?.response?.status === 401) {
                localStorage.removeItem('token');
                setUser(null);
            }
            return null;
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateRole,
        resendVerification,
        googleLogin,
        checkAuth,
        refreshUser,
        setUserData,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

export default AuthContext;
